<script lang="ts">
  import Button from "./shared/Button.svelte";
  import { onMount } from "svelte";
  import MessageModal from "./messageModal.svelte";

  let confirmReset = false;
  let confirmCatalogSwitch = false;
  type SettingsTab = "aws" | "hubspot" | "additional" | "sync" | "bulksync" | "cleanup" | "ai";
  let activeTab: SettingsTab = "aws";
  let hasHubspotSettings: boolean = false;
  let isSettingsNavCollapsed = false;
  const SETTINGS_NAV_COLLAPSED_KEY = "acelinkace-settings-nav-collapsed";

  const API_URL = `${process.env.SVELTE_APP_FE_REMOTE_URL}/api/configurations`;
  const SAVE_SETTINGS_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/sync/catalog-settings`;
  const SETTINGS_URL = `${process.env.SVELTE_APP_FE_REMOTE_URL}/api/common-config/settings`;
  const STAGES_URL = `${process.env.SVELTE_APP_ALADV_BASE_URL}/aladv/stages`;
  const DURATIONS_URL = `${process.env.SVELTE_APP_ALADV_BASE_URL}/aladv/durations`;
  const AI_PROVIDERS_URL = `${process.env.SVELTE_APP_ALADV_BASE_URL}/aladv/ai-providers`;
  const AI_MODELS_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/ai-models`;
  const BULK_SYNC_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/bulk-sync`;
  const CLEANUP_FINISHED_OPPORTUNITIES_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/cron/jobs/cleanup-finished-opportunities`;
  const ALADV_UNAVAILABLE_MESSAGE =
    "Validation settings service is temporarily unavailable. Please try again later.";
  const DEFAULT_ALADV_REQUEST_TIMEOUT_MS = 15000;

  type BulkSyncJobName = "bulksync-aws" | "cleanup-finished-opportunities";
  type BulkSyncForm = {
    schedule: string;
    timezone: string;
    enabled: boolean;
    minute: string;
    hour: string;
    dayOfMonth: string;
    month: string;
    dayOfWeek: string;
    loading: boolean;
    saving: boolean;
    message: string;
    messageType: "success" | "error";
    synced: boolean;
    runningSchedule: string;
    runningEnabled: boolean;
  };

  const bulkSyncJobs: Array<{
    name: BulkSyncJobName;
    label: string;
    description: string;
  }> = [
    {
      name: "bulksync-aws",
      label: "Bulk Sync From AWS",
      description: "Sync opportunities from AWS to shared database",
    },
    {
      name: "cleanup-finished-opportunities",
      label: "Finished Opportunities Cleanup",
      description: "Remove Closed Lost and Launched opportunities",
    },
  ];
  
  const awsFields = [
    "awsAccessKeyId",
    "awsSecretAccessKey",
    "awsRegion",
    "awsServiceName",
    "awsCatalog",
    "awsOppIdName",
  ];
  const hubspotFields = [
    "hubspotClientId",
    "hubspotClientSecret",
    "hubspotRedirectService",
    "hubspotRegService",
    "hubspotScopes",
    "hubspotAppId",
    "hubspotDeveloperApiKey",
    "hubspotWebhookUrl",
  ];
  const secretFields = [
    "awsAccessKeyId",
    "awsSecretAccessKey",
    "hubspotClientId",
    "hubspotClientSecret",
    "hubspotDeveloperApiKey",
    "aiApiKey",
  ];
  const additionalFields = ["additionalField1", "additionalField2"];
  const aiFields = ["aiProvider", "aiModel", "aiApiKey", "mcpServer"];
  const syncFields = ["syncStages", "syncDuration", "alwaysFillAwsMandatoryFields", "defaultMandatoryFieldContext"];

  const settingsSections: Array<{
    id: SettingsTab;
    label: string;
    description: string;
    iconPath: string;
    requiresHubspot?: boolean;
  }> = [
    { id: "aws", label: "AWS", description: "Credentials and catalog setup", iconPath: "M17.5 19H8a5 5 0 1 1 1.1-9.88A6 6 0 0 1 20.5 12.5 3.5 3.5 0 0 1 17.5 19Z" },
    { id: "sync", label: "Sync", description: "Stage and duration filters", iconPath: "M17 1l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3" },
    { id: "hubspot", label: "HubSpot", description: "CRM integration settings", iconPath: "M7 12a5 5 0 1 0 5-5M12 7V3M12 3h4M12 17v4M12 21H8M17 12h4M21 12v4" , requiresHubspot: true },
    { id: "bulksync", label: "Automation", description: "Scheduled AWS sync", iconPath: "M12 8v5l3 2M21 12a9 9 0 1 1-9-9" },
    { id: "cleanup", label: "Cleanup", description: "Finished opportunity cleanup", iconPath: "M3 6h18M8 6V4h8v2M6 6l1 16h10l1-16M10 11v6M14 11v6" },
    { id: "ai", label: "AI", description: "Provider, model, and MCP", iconPath: "M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3ZM18 15l.8 2.2L21 18l-2.2.8L18 21l-.8-2.2L15 18l2.2-.8L18 15Z" },
    { id: "additional", label: "Advanced", description: "Additional settings", iconPath: "M4 6h16M7 6v12M4 18h16M17 6v12M10 12h4" },
  ];

  $: visibleSettingsSections = settingsSections.filter(
    (section) => !section.requiresHubspot || hasHubspotSettings
  );
  $: activeSettingsSection =
    visibleSettingsSections.find((section) => section.id === activeTab) ?? visibleSettingsSections[0];

  const wideSettingsFields = new Set(["syncStages", "mcpServer", "alwaysFillAwsMandatoryFields", "defaultMandatoryFieldContext"]);

  const setActiveTab = (tab: SettingsTab): void => {
    activeTab = tab;
    if (tab === "cleanup") {
      fetchBulkSyncConfig("cleanup-finished-opportunities");
    }
  };

  const setSettingsNavCollapsed = (collapsed: boolean): void => {
    isSettingsNavCollapsed = collapsed;
    localStorage.setItem(SETTINGS_NAV_COLLAPSED_KEY, String(collapsed));
  };

  let config: Record<string, any> = {
    awsAccessKeyId: "",
    awsSecretAccessKey: "",
    awsRegion: "",
    awsServiceName: "",
    awsCatalog: "",
    awsOppIdName: "",
    targetCloseDateName: "",
    projectTitleName: "",
    hubspotClientId: "",
    hubspotClientSecret: "",
    hubspotRedirectService: "",
    hubspotRegService: "",
    hubspotScopes: "",
    hubspotAppId: "",
    hubspotDeveloperApiKey: "",
    hubspotWebhookUrl: "",
    additionalField1: "",
    additionalField2: "",
    aiProvider: "",
    aiModel: "",
    aiApiKey: "",
    mcpServer: "",
    syncStages: [] as string[],
    syncDuration: 12,
    alwaysFillAwsMandatoryFields: false,
    defaultMandatoryFieldContext: "",
  };

  let configId: number | null = null;
  let loadedAwsCatalog = "";
  let isLoading = false;
  let saveMessage = "";
  let saveMessageType: "success" | "error" = "success";
  let availableStages: Array<{ value: string; label: string ,isSelected: boolean }> = [];
  let availableDurations: Array<{ value: number; label: string }> = [];
  let availableAiProviders: Array<{ value: string; label: string }> = [];
  let availableAiModels: Array<{ value: string; label: string }> = [];
  let loadingStages = false;
  let loadingDurations = false;
  let loadingAiProviders = false;
  let loadingAiModels = false;
  let aladvSettingsError = "";

  let visibilityMap: Record<string, boolean> = {};
  let bulkSyncForms: Record<BulkSyncJobName, BulkSyncForm> = {
    "bulksync-aws": {
      schedule: "",
      timezone: "",
      enabled: false,
      minute: "",
      hour: "",
      dayOfMonth: "",
      month: "",
      dayOfWeek: "",
      loading: false,
      saving: false,
      message: "",
      messageType: "success",
      synced: true,
      runningSchedule: "",
      runningEnabled: false,
    },
    "cleanup-finished-opportunities": {
      schedule: "",
      timezone: "",
      enabled: false,
      minute: "",
      hour: "",
      dayOfMonth: "",
      month: "",
      dayOfWeek: "",
      loading: false,
      saving: false,
      message: "",
      messageType: "success",
      synced: true,
      runningSchedule: "",
      runningEnabled: false,
    },
  };

  const maskSecret = (value: string): string => {
    if (!value) return "";
    const len = value.length;
    if (len <= 4) return "•".repeat(len);
    return value.slice(0, 2) + "•".repeat(len - 4) + value.slice(len - 2);
  };

  const fetchAladvJson = async <T>(url: string, label: string): Promise<T | null> => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), DEFAULT_ALADV_REQUEST_TIMEOUT_MS);

    try {
      const res = await fetch(url, {
        headers: { accept: "application/json" },
        signal: controller.signal,
      });

      if (!res.ok) {
        throw new Error(`${label} request failed: ${res.status}`);
      }

      return (await res.json()) as T;
    } catch (err) {
      console.error(`Error loading ${label}:`, err);
      aladvSettingsError = ALADV_UNAVAILABLE_MESSAGE;
      return null;
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const toggleVisibility = (field: string) => {
    visibilityMap[field] = !visibilityMap[field];
  };

  const toBooleanSetting = (value: unknown): boolean =>
    value === true || value === 1 || String(value ?? "").toLowerCase() === "true";

  const fetchConfiguration = async () => {
    try {
      const res = await fetch(SETTINGS_URL);
      if (!res.ok) throw new Error("Failed to fetch configuration");
      const data = await res.json();
      configId = data?.id ?? null;
      const { id, ...rest } = data;
      
      config = {
        ...config,
        ...rest,
        awsCatalog: (data.awsCatalog || "") === "AWS" ? "prd" : "sandbox",
        syncStages: rest.syncStages || [],
        syncDuration: rest.syncDuration || 12,
        alwaysFillAwsMandatoryFields: toBooleanSetting(rest.alwaysFillAwsMandatoryFields),
        defaultMandatoryFieldContext: rest.defaultMandatoryFieldContext || ""
      };
      loadedAwsCatalog = config.awsCatalog;
      
      secretFields.forEach((field) => {
        visibilityMap[field] = !config[field] || config[field].trim() === "";
      });
    } catch (err) {
      console.error("Error loading configuration:", err);
    }
  };

  const saveConfiguration = async () => saveConfigurationInternal(false);

  const saveConfigurationInternal = async (skipCatalogConfirmation: boolean) => {
    isLoading = true;
    saveMessage = "";
    try {
      const nextAwsCatalog = config.awsCatalog === "prd" ? "prd" : "sandbox";
      const catalogChanged = !!loadedAwsCatalog && loadedAwsCatalog !== nextAwsCatalog;
      if (catalogChanged && !skipCatalogConfirmation) {
        confirmCatalogSwitch = true;
        return;
      }

      if (catalogChanged) {
        const catalogRes = await fetch(SAVE_SETTINGS_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ awsCatalog: nextAwsCatalog }),
        });
        if (!catalogRes.ok) throw new Error();
      }

      const method = configId ? "PUT" : "POST";
      const endpoint = configId ? `${API_URL}/${configId}` : API_URL;
      const res = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...config, awsCatalog: nextAwsCatalog }),
      });
      if (!res.ok) throw new Error();
      const data = res.status !== 204 ? await res.json() : null;
      if (!configId && data?.id) configId = data.id;
      loadedAwsCatalog = nextAwsCatalog;

      saveMessage = "Configuration saved";
      saveMessageType = "success";
      const payload = { mcpServer: config.mcpServer };
      console.log("[configSettings] Dispatched ai-config-updated with detail:", payload);
      window.dispatchEvent(new CustomEvent("ai-config-updated", { detail: payload }));
    } catch {
      saveMessage = "Failed to save configuration";
      saveMessageType = "error";
    } finally {
      isLoading = false;
    }
  };

  const confirmAndSaveCatalogSwitch = async () => {
    confirmCatalogSwitch = false;
    await saveConfigurationInternal(true);
  };

  const resetForm = async () => {
    try {
      Object.keys(config).forEach((key) => (config[key] = ""));
      config.alwaysFillAwsMandatoryFields = false;
      config.defaultMandatoryFieldContext = "";
      config.syncStages = [];
      config.syncDuration = 12;
      if (configId) {
        await fetch(`${API_URL}/${configId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(config),
        });
      }

      const defaultsRes = await fetch(SETTINGS_URL);
      if (!defaultsRes.ok) throw new Error("Failed to fetch defaults");

      const defaultsData = await defaultsRes.json();
      configId = defaultsData?.id ?? configId;
      const { id, ...rest } = defaultsData;
      config = { ...config, ...rest, awsCatalog: (config.awsCatalog || "") === "AWS" ? "prd" : "sandbox" };

      saveMessage = "Reset to default values";
      saveMessageType = "success";
      window.dispatchEvent(new CustomEvent("ai-config-updated", { detail: config }));
    } catch (err) {
      console.error("Reset failed:", err);
      saveMessage = "Failed to reset";
      saveMessageType = "error";
    }
  };

  const confirmAndReset = async () => {
    confirmReset = false;
    await resetForm();
  };

  const formatFieldLabel = (field: string): string =>
    field === "defaultMandatoryFieldContext"
      ? "Default Fill Context"
      : field
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^aws/, "AWS")
      .replace(/^hubspot/, "HubSpot")
      .replace(/^sync/, "Sync")
      .replace(/^ai/, "AI")
      .replace(/^mcp/, "MCP")
      .replace(/id$/i, "ID")
      .replace(/url$/i, "URL")
      .replace(/api$/i, "API")
      .replace(/uri$/i, "URI");

  const fetchAvailableStages = async () => {
    loadingStages = true;
    try {
      const data = await fetchAladvJson<any[]>(`${STAGES_URL}?filter=%7B%0A%0A%7D`, "stages");
      if (Array.isArray(data)) {
        availableStages = data.map(item => ({
          value: item.name,
          label: item.name,
          isSelected: item?.isDefault === 1 ? true : false,
        }));
      }
    } finally {
      loadingStages = false;
    }
  };

  const fetchAvailableDurations = async () => {
    loadingDurations = true;
    try {
      const data = await fetchAladvJson<any[]>(
        `${DURATIONS_URL}?filter=%7B%0A%0A%7D`,
        "durations"
      );
      if (Array.isArray(data)) {
        availableDurations = data.map(item => ({
          value: item.value,
          label: item.name
        }));
      }
    } finally {
      loadingDurations = false;
    }
  };

  const fetchAvailableAiProviders = async () => {
    loadingAiProviders = true;
    try {
      const data = await fetchAladvJson<any[]>(AI_PROVIDERS_URL, "AI providers");
      if (Array.isArray(data)) {
        availableAiProviders = data
          .map((item) => ({
            value: item.value,
            label: item.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
      }
    } finally {
      loadingAiProviders = false;
    }
  };

  const fetchAvailableAiModels = async (provider = "") => {
    loadingAiModels = true;
    try {
      if (!provider) {
        availableAiModels = [];
        return;
      }

      const res = await fetch(AI_MODELS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider }),
      });
      if (!res.ok) throw new Error(`Failed to fetch AI models (${res.status})`);

      const data = await res.json();
      const models = data.models || [];
      if (Array.isArray(models)) {
        availableAiModels = models
          .map((item) => ({
            value: item.value,
            label: item.label,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
      } else {
        availableAiModels = [];
      }
    } finally {
      loadingAiModels = false;
    }
  };

  const setBulkSyncForm = (jobName: BulkSyncJobName, updates: Partial<BulkSyncForm>) => {
    bulkSyncForms = {
      ...bulkSyncForms,
      [jobName]: { ...bulkSyncForms[jobName], ...updates },
    };
  };

  const getInputValue = (event: Event): string => (event.currentTarget as HTMLInputElement).value;
  const getInputChecked = (event: Event): boolean =>
    (event.currentTarget as HTMLInputElement).checked;

  const parseSchedule = (
    schedule: string,
    fallback: { minute: string; hour: string; dayOfMonth: string; month: string; dayOfWeek: string }
  ): { minute: string; hour: string; dayOfMonth: string; month: string; dayOfWeek: string } => {
    const parts = schedule.trim().split(/\s+/);
    if (parts.length < 5) return fallback;
    return {
      minute: parts[0] || fallback.minute,
      hour: parts[1] || fallback.hour,
      dayOfMonth: parts[2] || fallback.dayOfMonth,
      month: parts[3] || fallback.month,
      dayOfWeek: parts[4] || fallback.dayOfWeek,
    };
  };

  const buildSchedule = (
    minute: string,
    hour: string,
    dayOfMonth: string,
    month: string,
    dayOfWeek: string
  ): string => {
    const normalizedMinute = minute?.trim() || "0";
    const normalizedHour = hour?.trim() || "0";
    const normalizedDayOfMonth = dayOfMonth?.trim() || "*";
    const normalizedMonth = month?.trim() || "*";
    const normalizedDayOfWeek = dayOfWeek?.trim() || "*";
    return `${normalizedMinute} ${normalizedHour} ${normalizedDayOfMonth} ${normalizedMonth} ${normalizedDayOfWeek}`;
  };

  const updateScheduleFromParts = (
    jobName: BulkSyncJobName,
    updates: Partial<Pick<BulkSyncForm, "minute" | "hour" | "dayOfMonth" | "month" | "dayOfWeek">>
  ) => {
    const form = bulkSyncForms[jobName];
    const minute = updates.minute ?? form.minute;
    const hour = updates.hour ?? form.hour;
    const dayOfMonth = updates.dayOfMonth ?? form.dayOfMonth;
    const month = updates.month ?? form.month;
    const dayOfWeek = updates.dayOfWeek ?? form.dayOfWeek;
    const schedule = buildSchedule(minute, hour, dayOfMonth, month, dayOfWeek);
    setBulkSyncForm(jobName, { minute, hour, dayOfMonth, month, dayOfWeek, schedule });
  };

  const getBulkSyncJobUrl = (jobName: BulkSyncJobName): string =>
    jobName === "cleanup-finished-opportunities" ? CLEANUP_FINISHED_OPPORTUNITIES_URL : BULK_SYNC_URL;

  const fetchBulkSyncConfig = async (jobName: BulkSyncJobName) => {
    setBulkSyncForm(jobName, { loading: true, message: "" });
    try {
      const res = await fetch(`${getBulkSyncJobUrl(jobName)}/config`);
      if (!res.ok) throw new Error("Failed to fetch bulk sync config");
      const data = await res.json();

      const dbConfig = data?.db;
      const runningConfig = data?.running;
      const synced = data?.synced ?? true;

      const scheduleValue = dbConfig?.schedule || "";
      const parsed = parseSchedule(scheduleValue, {
        minute: "",
        hour: "",
        dayOfMonth: "",
        month: "",
        dayOfWeek: "",
      });
      setBulkSyncForm(jobName, {
        schedule: scheduleValue,
        timezone: dbConfig?.timezone || "",
        enabled: dbConfig?.enabled ? dbConfig.enabled > 0 : false,
        synced,
        runningSchedule: runningConfig?.schedule || "",
        runningEnabled: runningConfig?.enabled ? runningConfig.enabled > 0 : false,
        ...parsed,
      });
    } catch (err) {
      console.error("Error loading automation config:", err);
      setBulkSyncForm(jobName, { message: "Failed to load automation config", messageType: "error" });
    } finally {
      setBulkSyncForm(jobName, { loading: false });
    }
  };

  const saveBulkSyncConfig = async (jobName: BulkSyncJobName) => {
    const form = bulkSyncForms[jobName];
    setBulkSyncForm(jobName, { saving: true, message: "" });
    try {
      const schedule = buildSchedule(
        form.minute,
        form.hour,
        form.dayOfMonth,
        form.month,
        form.dayOfWeek
      );
      const payload = {
        schedule,
        timezone: form.timezone,
        enabled: form.enabled ? 1 : 0,
      };

      const res = await fetch(`${getBulkSyncJobUrl(jobName)}/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(
          jobName === "cleanup-finished-opportunities"
            ? "Failed to save cleanup scheduler config"
            : "Failed to save bulk sync config",
        );
      }
      const data = await res.json();
      if (data?.config) {
        const updatedSchedule = data.config.schedule || schedule;
        const parsed = parseSchedule(updatedSchedule, {
          minute: form.minute,
          hour: form.hour,
          dayOfMonth: form.dayOfMonth,
          month: form.month,
          dayOfWeek: form.dayOfWeek,
        });
        setBulkSyncForm(jobName, {
          schedule: updatedSchedule,
          timezone: data.config.timezone || form.timezone,
          enabled: data.config.enabled ? data.config.enabled > 0 : form.enabled,
          ...parsed,
        });
      } else {
        setBulkSyncForm(jobName, { schedule });
      }

      const msg = jobName === "cleanup-finished-opportunities"
        ? "Schedule saved."
        : form.enabled
          ? "Schedule saved. Sync started in background."
          : "Schedule saved.";
      setBulkSyncForm(jobName, {
        message: msg,
        messageType: "success",
        synced: true,
        runningSchedule: form.schedule,
        runningEnabled: form.enabled,
      });
    } catch (err) {
      const isCleanupJob = jobName === "cleanup-finished-opportunities";
      console.error(isCleanupJob ? "Cleanup scheduler save failed:" : "Bulk sync save failed:", err);
      setBulkSyncForm(jobName, {
        message: isCleanupJob ? "Failed to save cleanup scheduler settings" : "Failed to save bulk sync settings",
        messageType: "error",
      });
    } finally {
      setBulkSyncForm(jobName, { saving: false });
    }
  };

  function toggleStage(stageValue: string) {
    if (!Array.isArray(config.syncStages)) {
      config.syncStages = [];
    }
    
    const index = config.syncStages.indexOf(stageValue);
    if (index > -1) {
      // Remove the stage
      config.syncStages.splice(index, 1);
      config.syncStages = config.syncStages; // Trigger reactivity
    } else {
      // Add the stage
      config.syncStages.push(stageValue);
      config.syncStages = config.syncStages; // Trigger reactivity
    }
  }

  function isStageSelected(stageValue: string): boolean {
    if (!Array.isArray(config.syncStages)) return false;
    return config.syncStages.includes(stageValue);
  }

  const handleAiProviderChange = async (event: Event) => {
    const nextProvider = (event.currentTarget as HTMLSelectElement).value;
    config.aiProvider = nextProvider;
    config.aiModel = "";
    await fetchAvailableAiModels(nextProvider);
  };

  // Reactive variable for selected stages count
  $: selectedStagesCount = Array.isArray(config.syncStages) ? config.syncStages.length : 0;

  onMount(async () => {
    isSettingsNavCollapsed = localStorage.getItem(SETTINGS_NAV_COLLAPSED_KEY) === "true";

    await fetchConfiguration();
    aladvSettingsError = "";
    await Promise.all([
      fetchAvailableStages(),
      fetchAvailableDurations(),
      fetchAvailableAiProviders(),
      fetchAvailableAiModels(config.aiProvider || "")
    ]);
    
    // After everything is loaded, select all stages by default except "Launched" and "Closed Lost"
    if (!config.syncStages || config.syncStages.length === 0) {
      config.syncStages = availableStages
        .filter(stage => stage.isSelected)
        .map(stage => stage.value);
    }

    await fetchBulkSyncConfig("bulksync-aws");
  });
</script>

<div class="mx-auto w-full">
  <h2 class="mb-4 mt-2 max-w-full break-words text-lg font-bold leading-tight text-white sm:mb-5 sm:mt-0 sm:text-2xl">
    AcelinkACE Configurations
  </h2>

  <div
    class:collapsed-settings-nav={isSettingsNavCollapsed}
    class="settings-shell grid min-h-[calc(100vh-170px)] rounded-xl shadow-sm"
  >
    <aside class="flex flex-col justify-between border-r border-secondary-700 bg-secondary-800 p-2 md:p-3">
      <nav class="flex flex-col gap-1" aria-label="Settings sections">
        {#each visibleSettingsSections as section}
          <button
            type="button"
            class={`settings-nav-item flex items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors md:px-4 ${
              activeTab === section.id
                ? "bg-primary-600 text-white"
                : "text-secondary-300 hover:bg-secondary-700 hover:text-white"
            }`}
            on:click={() => setActiveTab(section.id)}
            title={section.label}
          >
            {#if isSettingsNavCollapsed}
              <span class="flex justify-center">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d={section.iconPath} stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </span>
            {:else}
              <span class="flex items-center gap-2 md:hidden">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d={section.iconPath} stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span class="settings-nav-label text-sm font-semibold">{section.label}</span>
              </span>
              <span class="settings-nav-copy hidden items-start gap-3 md:flex">
                <svg class="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d={section.iconPath} stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span>
                  <span class="block text-sm font-semibold">{section.label}</span>
                  <span class="settings-nav-description mt-0.5 block text-xs text-secondary-400">{section.description}</span>
                </span>
              </span>
            {/if}
          </button>
        {/each}
      </nav>

      <button
        type="button"
        class="settings-bottom-toggle mt-4 flex h-10 w-10 items-center justify-center rounded-lg border border-secondary-600 bg-secondary-800 text-secondary-300 hover:border-primary-500 hover:bg-secondary-700 hover:text-white"
        on:click={() => setSettingsNavCollapsed(!isSettingsNavCollapsed)}
        title={isSettingsNavCollapsed ? "Expand navigation" : "Collapse navigation"}
      >
        <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          {#if isSettingsNavCollapsed}
            <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round" />
          {:else}
            <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round" />
          {/if}
        </svg>
      </button>
    </aside>

    <form on:submit|preventDefault={saveConfiguration} class="settings-content space-y-5 p-4 sm:p-6 lg:p-7">
      <div class="border-b border-secondary-800 pb-4">
        <h3 class="text-lg font-semibold text-white">{activeSettingsSection?.label}</h3>
        <p class="mt-1 text-sm text-secondary-400">{activeSettingsSection?.description}</p>
      </div>

      {#if aladvSettingsError && (activeTab === "sync" || activeTab === "ai")}
        <div class="p-3 rounded bg-red-100 text-red-700 text-sm">
          {aladvSettingsError}
        </div>
      {/if}

      {#if activeTab !== "bulksync" && activeTab !== "cleanup"}
        <div class="grid grid-cols-1 gap-x-8 gap-y-5 lg:grid-cols-2">
          {#each Object.entries(config) as [field, value] (field)}
            {#if (activeTab === "aws" && awsFields.includes(field)) || (activeTab === "hubspot" && hubspotFields.includes(field)) || (activeTab === "additional" && additionalFields.includes(field)) || (activeTab === "ai" && aiFields.includes(field)) || (activeTab === "sync" && syncFields.includes(field))}
              {#if activeTab === "sync" && field === "alwaysFillAwsMandatoryFields"}
                <div class="lg:col-span-2 pt-3 border-t border-secondary-800">
                  <h4 class="text-sm font-semibold text-white">AWS mandatory field fill</h4>
                  <p class="mt-1 text-xs text-secondary-400">AI assistance for AWS Partner Central mandatory fields.</p>
                </div>
              {/if}
              <div class={wideSettingsFields.has(field) ? "lg:col-span-2" : ""}>
                {#if field !== "alwaysFillAwsMandatoryFields"}
                  <label class="block text-sm font-medium text-secondary-300 mb-1" for={field}>
                    {formatFieldLabel(field)}
                  </label>
                {/if}
                <div>
            {#if field === "awsCatalog"}
              <select
                id="awsCatalog"
                bind:value={config.awsCatalog}
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="sandbox">Sandbox</option>
                <option value="prd">AWS</option>
              </select>
            {:else if secretFields.includes(field)}
              <div class="relative">
                {#if visibilityMap[field]}
                  <input
                    id={field}
                    type="text"
                    class="w-full border rounded px-3 pr-10 py-2 focus:ring-2 focus:ring-blue-500"
                    bind:value={config[field]}
                    placeholder={`Enter ${formatFieldLabel(field)}`}
                  />
                {:else}
                  <input
                    id={field}
                    type="text"
                    class="w-full border rounded px-3 pr-10 py-2 text-secondary-400 bg-secondary-900 focus:ring-2 focus:ring-blue-500"
                    value={maskSecret(config[field])}
                    readonly
                    placeholder={`Enter ${formatFieldLabel(field)}`}
                  />
                {/if}

                <!-- Eye button -->
                <button
                  type="button"
                  class="absolute inset-y-0 right-2 flex items-center text-secondary-500 hover:text-secondary-400 focus:outline-none"
                  on:click={() => toggleVisibility(field)}
                >
                  {#if visibilityMap[field]}
                    <!-- Eye Off (hide) -->
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5 sm:h-6 sm:w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-5-10-7a10.05 10.05 0 012.458-3.825m3.877-2.672A9.956 9.956 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.956 9.956 0 01-4.132 5.411M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 3l18 18"
                      />
                    </svg>
                  {:else}
                    <!-- Eye (show) -->
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5 sm:h-6 sm:w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  {/if}
                </button>
              </div>
            {:else if field === "syncStages"}
              <div class="border border-secondary-600 rounded-lg overflow-hidden">
                <!-- Header with count -->
                <div class="bg-secondary-900 px-3 py-2 border-b border-secondary-600">
                  <span class="text-sm font-medium text-secondary-300">
                    {loadingStages ? "Loading..." : `${selectedStagesCount} of ${availableStages.length} selected`}
                  </span>
                </div>
                <!-- Checkbox List -->
                <div class="max-h-48 overflow-y-auto">
                  {#if loadingStages}
                    <div class="px-3 py-4 text-center text-secondary-400">
                      <span>Loading stages...</span>
                    </div>
                  {:else if availableStages.length === 0}
                    <div class="px-3 py-4 text-center text-secondary-400">
                      <span>No stages available</span>
                    </div>
                  {:else}
                    {#each availableStages as stage}
                      <label class="flex items-center px-3 py-2.5 hover:bg-secondary-700 cursor-pointer border-b border-secondary-800 last:border-b-0 transition-colors">
                        <input
                          type="checkbox"
                          value={stage.value}
                          checked={isStageSelected(stage.value)}
                          on:change={() => toggleStage(stage.value)}
                          class="w-4 h-4 text-blue-600 border-secondary-600 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
                        />
                        <span class="ml-3 text-sm text-secondary-300 select-none">{stage.label}</span>
                      </label>
                    {/each}
                  {/if}
                </div>
              </div>
              <p class="mt-1 text-xs text-secondary-400">
                Select stages to sync from AWS during sync operations
              </p>
            {:else if field === "syncDuration"}
              <select
                id={field}
                bind:value={config[field]}
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                disabled={loadingDurations}
              >
                {#if loadingDurations}
                  <option value="">Loading durations...</option>
                {:else if availableDurations.length === 0}
                  <option value="">No durations available</option>
                {:else}
                  {#each availableDurations as duration}
                    <option value={duration.value}>{duration.label}</option>
                  {/each}
                {/if}
              </select>
              <p class="mt-1 text-xs text-secondary-400">
                Sync opportunities modified within the selected duration
              </p>
            {:else if field === "alwaysFillAwsMandatoryFields"}
              <div class="rounded-lg border border-secondary-700 bg-secondary-900/40 px-4 py-4">
                <label class="flex items-start gap-3 cursor-pointer">
                  <input
                    id={field}
                    type="checkbox"
                    bind:checked={config[field]}
                    class="mt-1 w-4 h-4 text-blue-600 border-secondary-600 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <span>
                    <span class="block text-sm font-semibold text-white">Auto-fill AWS mandatory fields</span>
                    <span class="block mt-1 text-sm text-secondary-400">
                      After an opportunity update, use AI to fill missing AWS mandatory fields before syncing.
                    </span>
                  </span>
                </label>
              </div>
            {:else if field === "defaultMandatoryFieldContext"}
              <textarea
                id={field}
                bind:value={config[field]}
                rows="4"
                maxlength="5000"
                placeholder="e.g. If no country is defined, use Switzerland."
                class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              ></textarea>
              <p class="mt-1 text-xs text-secondary-400">
                Applied as default context to every AWS mandatory field fill. Specific opportunity data always takes precedence.
              </p>
            {:else if field === "aiProvider"}
              <select
                id={field}
                value={config[field]}
                on:change={handleAiProviderChange}
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                disabled={loadingAiProviders}
              >
                {#if loadingAiProviders}
                  <option value="">Loading AI providers...</option>
                {:else if availableAiProviders.length === 0}
                  <option value="">No AI providers available</option>
                {:else}
                  <option value="">Select AI provider</option>
                  {#each availableAiProviders as provider}
                    <option value={provider.value}>{provider.label}</option>
                  {/each}
                {/if}
              </select>
            {:else if field === "aiModel"}
              <select
                id={field}
                bind:value={config[field]}
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                disabled={loadingAiModels || !config.aiProvider}
              >
                {#if loadingAiModels}
                  <option value="">Loading AI models...</option>
                {:else if !config.aiProvider}
                  <option value="">Select AI provider first</option>
                {:else if availableAiModels.length === 0}
                  <option value="">No AI models available</option>
                {:else}
                  <option value="">Select AI model</option>
                  {#each availableAiModels as model}
                    <option value={model.value}>{model.label}</option>
                  {/each}
                {/if}
              </select>
            {:else if field === "mcpServer"}
              <select
                id={field}
                bind:value={config[field]}
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="/mcp/acelink-mcp">Acelink</option>
                <option value="/mcp/mcp-aws-apn">AWS APN</option>
                <option value="/mcp/acelink-plus-aws-apn">Acelink + AWS APN</option>
              </select>
            {:else}
              <input
                id={field}
                type="text"
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                bind:value={config[field]}
                placeholder={`Enter ${formatFieldLabel(field)}`}
              />
              {#if field === "awsOppIdName"}
                <p class="mt-1 text-xs text-secondary-400">
                  Leave empty to use the default field. Enter a custom field name if your HubSpot already has an AWS Opportunity ID field.
                </p>
              {/if}
            {/if}
            {#if field === "aiApiKey"}
              <p class="mt-1 text-xs text-secondary-400">
                Paste the API key of the AI service you want to use. This is saved here.
              </p>
            {:else if field === "aiProvider"}
              <p class="mt-1 text-xs text-secondary-400">
                Choose the AI service you want to use.
              </p>
            {:else if field === "aiModel"}
              <p class="mt-1 text-xs text-secondary-400">
                Choose the model for the selected AI service.
              </p>
            {:else if field === "mcpServer"}
              <p class="mt-1 text-xs text-secondary-300">
                Enter the full API endpoint URL of the MCP Server. Example: <code>/mcp</code> or <code>https://mcp.example.com</code>.
              </p>
            {/if}
              </div>
            </div>
          {/if}
        {/each}
      </div>
    {/if}

    {#if activeTab === "bulksync" || activeTab === "cleanup"}
      <div class="flex flex-col gap-4">
          {#each bulkSyncJobs.filter((job) => activeTab === "cleanup" ? job.name === "cleanup-finished-opportunities" : job.name === "bulksync-aws") as job}
            <div class="overflow-hidden">
              {#if bulkSyncForms[job.name].loading}
                <div class="flex items-center justify-center py-16">
                  <svg class="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              {:else}
              {#if !bulkSyncForms[job.name].synced}
                <div class="px-4 sm:px-6 py-3 bg-yellow-50 border-b border-yellow-300">
                  <div class="flex flex-wrap items-center justify-between gap-2">
                    <div class="flex items-center gap-2 text-yellow-900">
                      <svg class="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                      </svg>
                      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                        Config out of sync
                      </span>
                      <span class="text-xs text-yellow-800">
                        Running: <code class="bg-yellow-100 px-1 rounded">{bulkSyncForms[job.name].runningSchedule || "not set"}</code>
                        ({bulkSyncForms[job.name].runningEnabled ? "enabled" : "disabled"})
                      </span>
                    </div>
                    <span class="text-xs text-yellow-800">Save to apply changes.</span>
                  </div>
                </div>
              {/if}

                <div class="px-0 sm:px-6 py-4 sm:py-5 border-b border-secondary-800">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div class="flex min-w-0 items-start gap-3">
                    <div class="hidden w-10 h-10 rounded-lg bg-primary-100 sm:flex items-center justify-center flex-shrink-0">
                      <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {#if job.name === "cleanup-finished-opportunities"}
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 6h18M8 6V4h8v2m-9 0l1 14h8l1-14" />
                        {:else}
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        {/if}
                      </svg>
                    </div>
                    <div class="min-w-0">
                      <h4 class="text-base sm:text-lg font-semibold text-white">{job.label}</h4>
                      <p class="text-sm text-secondary-400">{job.description}</p>
                    </div>
                  </div>
                  <div class="flex items-center gap-3 self-start sm:self-auto">
                    <span class="text-sm text-secondary-400">{bulkSyncForms[job.name].enabled ? 'Enabled' : 'Disabled'}</span>
                    <button
                      type="button"
                      class={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${bulkSyncForms[job.name].enabled ? 'bg-primary-600' : 'bg-secondary-600'}`}
                      on:click={() => setBulkSyncForm(job.name, { enabled: !bulkSyncForms[job.name].enabled })}
                    >
                      <span class="sr-only">Enable automation</span>
                      <span
                        class={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-secondary-800 shadow ring-0 transition duration-200 ease-in-out ${bulkSyncForms[job.name].enabled ? 'translate-x-5' : 'translate-x-0'}`}
                      ></span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="px-0 sm:px-6 py-4 sm:py-5 space-y-5">
                <div>
                  <div class="flex items-center gap-2 mb-3">
                    <svg class="w-4 h-4 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span class="text-sm font-medium text-secondary-300">Schedule</span>
                  </div>
                  <div class="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-5">
                    <div>
                      <label class="block text-xs font-medium text-secondary-400 mb-1.5" for={`bulk-sync-minute-${job.name}`}>Minute</label>
                      <input
                        id={`bulk-sync-minute-${job.name}`}
                        type="text"
                        value={bulkSyncForms[job.name].minute}
                        on:input={(e) => updateScheduleFromParts(job.name, { minute: getInputValue(e) })}
                        placeholder="0"
                        class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label class="block text-xs font-medium text-secondary-400 mb-1.5" for={`bulk-sync-hour-${job.name}`}>Hour</label>
                      <input
                        id={`bulk-sync-hour-${job.name}`}
                        type="text"
                        value={bulkSyncForms[job.name].hour}
                        on:input={(e) => updateScheduleFromParts(job.name, { hour: getInputValue(e) })}
                        placeholder="2"
                        class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label class="block text-xs font-medium text-secondary-400 mb-1.5" for={`bulk-sync-dom-${job.name}`}>Day</label>
                      <input
                        id={`bulk-sync-dom-${job.name}`}
                        type="text"
                        value={bulkSyncForms[job.name].dayOfMonth}
                        on:input={(e) => updateScheduleFromParts(job.name, { dayOfMonth: getInputValue(e) })}
                        placeholder="*"
                        class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label class="block text-xs font-medium text-secondary-400 mb-1.5" for={`bulk-sync-month-${job.name}`}>Month</label>
                      <input
                        id={`bulk-sync-month-${job.name}`}
                        type="text"
                        value={bulkSyncForms[job.name].month}
                        on:input={(e) => updateScheduleFromParts(job.name, { month: getInputValue(e) })}
                        placeholder="*"
                        class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label class="block text-xs font-medium text-secondary-400 mb-1.5" for={`bulk-sync-dow-${job.name}`}>Weekday</label>
                      <input
                        id={`bulk-sync-dow-${job.name}`}
                        type="text"
                        value={bulkSyncForms[job.name].dayOfWeek}
                        on:input={(e) => updateScheduleFromParts(job.name, { dayOfWeek: getInputValue(e) })}
                        placeholder="*"
                        class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      />
                    </div>
                  </div>
                  <div class="text-xs text-secondary-500 mt-3 space-y-1">
                    <p class="font-medium text-secondary-400">Examples:</p>
                    <div class="grid grid-cols-1 gap-1 break-words sm:grid-cols-2">
                      <span><code class="inline-block bg-secondary-700 px-1.5 py-0.5 rounded text-secondary-400">0 6 * * *</code> Daily at 6:00 AM</span>
                      <span><code class="inline-block bg-secondary-700 px-1.5 py-0.5 rounded text-secondary-400">0 2 * * *</code> Daily at 2:00 AM</span>
                      <span><code class="inline-block bg-secondary-700 px-1.5 py-0.5 rounded text-secondary-400">30 8 * * *</code> Daily at 8:30 AM</span>
                      <span><code class="inline-block bg-secondary-700 px-1.5 py-0.5 rounded text-secondary-400">0 0 * * 0</code> Weekly on Sunday</span>
                    </div>
                  </div>
                </div>

                <div>
                  <div class="flex items-center gap-2 mb-3">
                    <svg class="w-4 h-4 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span class="text-sm font-medium text-secondary-300">Timezone</span>
                  </div>
                  <input
                    type="text"
                    id={`bulk-sync-timezone-${job.name}`}
                    value={bulkSyncForms[job.name].timezone}
                    on:input={(e) => setBulkSyncForm(job.name, { timezone: getInputValue(e) })}
                    placeholder="UTC"
                    class="w-full sm:max-w-xs border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                  <p class="text-xs text-secondary-500 mt-2">e.g. UTC, America/New_York</p>
                </div>
              </div>

              <div class="px-0 sm:px-6 py-4 space-y-3">
                {#if bulkSyncForms[job.name].message}
                  <div class={`flex items-center gap-2 text-sm ${bulkSyncForms[job.name].messageType === "success" ? "text-green-600" : "text-red-600"}`}>
                    {#if bulkSyncForms[job.name].messageType === "success"}
                      <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                      </svg>
                    {:else}
                      <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                      </svg>
                    {/if}
                    <span>{bulkSyncForms[job.name].message}</span>
                  </div>
                {/if}
                <Button
                  size="lg"
                  disabled={bulkSyncForms[job.name].saving || bulkSyncForms[job.name].loading}
                  on:click={() => saveBulkSyncConfig(job.name)}
                >
                  {#if bulkSyncForms[job.name].saving}
                    <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  {:else}
                    Save Schedule
                  {/if}
                </Button>
              </div>
              {/if}
            </div>
          {/each}
      </div>
    {/if}

    {#if activeTab !== "bulksync" && activeTab !== "cleanup"}
      {#if saveMessage}
        <div
          class={`p-3 rounded ${
            saveMessageType === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
          }`}
        >
          {saveMessage}
        </div>
      {/if}

      <div class="flex flex-wrap justify-start gap-3 border-t border-secondary-800 pt-5">
        <Button type="submit" size="lg" disabled={isLoading}>
          {isLoading ? "Saving..." : "Save"}
        </Button>
        <Button variant="secondary" size="lg" on:click={() => (confirmReset = true)} disabled={isLoading}>
          Reset
        </Button>
      </div>
    {/if}
    </form>
  </div>
</div>

<style>
  .settings-shell :global(code) {
    background-color: #343a52 !important;
    color: #dbe3f0 !important;
  }

  .settings-shell :global(.bg-secondary-800) {
    background-color: #24283a !important;
  }
  .settings-shell :global(.bg-secondary-900) {
    background-color: #1e2233 !important;
  }
  .settings-shell :global(.bg-secondary-700),
  .settings-shell :global(.hover\:bg-secondary-700:hover) {
    background-color: #343a52 !important;
  }
  .settings-shell :global(.bg-secondary-600) {
    background-color: #3d425b !important;
  }
  .settings-shell :global(.bg-gray-200) {
    background-color: #3d425b !important;
  }
  .settings-shell :global(.border-secondary-800) {
    border-color: #2c3145 !important;
  }
  .settings-shell :global(.border-secondary-700) {
    border-color: #383e55 !important;
  }
  .settings-shell :global(.border-secondary-600) {
    border-color: #444a63 !important;
  }

  .settings-shell :global(input:not([type="checkbox"])),
  .settings-shell :global(select),
  .settings-shell :global(textarea) {
    background-color: #1a1e2f;
    color: #f1f5f9;
    border-color: #383e55;
    color-scheme: dark;
  }

  .settings-shell :global(input::placeholder),
  .settings-shell :global(textarea::placeholder) {
    color: #64748b;
  }

  .settings-shell {
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr);
    min-height: calc(100vh - 170px);
    border: 1px solid rgba(226, 232, 240, 0.16);
    border-radius: 0.75rem;
    background: #24283a;
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
    overflow-x: auto;
  }

  .settings-shell :global(.settings-content) {
    min-width: 0;
    overflow: hidden;
  }

  .settings-shell.collapsed-settings-nav {
    grid-template-columns: 88px minmax(0, 1fr);
  }

  .settings-shell.collapsed-settings-nav :global(.settings-nav-item) {
    justify-content: center;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .settings-shell.collapsed-settings-nav :global(.settings-nav-copy) {
    display: none;
  }

  .settings-shell.collapsed-settings-nav :global(.settings-bottom-toggle) {
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: 767px) {
    .settings-shell {
      grid-template-columns: 112px minmax(0, 1fr);
    }

    .settings-shell.collapsed-settings-nav {
      grid-template-columns: 56px minmax(0, 1fr);
    }

    .settings-shell :global(.settings-nav-description) {
      display: none;
    }

    .settings-shell :global(.settings-nav-item) {
      gap: 0.5rem;
      min-width: 0;
      overflow: hidden;
      padding-left: 0.375rem;
      padding-right: 0.375rem;
    }

    .settings-shell :global(.settings-nav-label) {
      display: inline-block;
      max-width: 3.25rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 0.8125rem;
    }

    .settings-shell :global(.settings-nav-copy) {
      min-width: 0;
      overflow: hidden;
    }

    .settings-shell :global(.settings-bottom-toggle) {
      margin-left: auto;
      margin-right: auto;
    }
  }
</style>

<!-- Reset Confirmation -->
{#if confirmReset}
  <div class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
    <div class="bg-secondary-800 rounded-xl shadow-lg p-6 max-w-sm w-full text-center">
      <h2 class="text-lg font-semibold mb-2 text-white">Reset Configuration</h2>
      <p class="text-secondary-400">Are you sure you want to reset to defaults?</p>
      <div class="flex justify-center gap-4 mt-4">
        <Button variant="danger" on:click={confirmAndReset}>Yes, Reset</Button>
        <Button variant="secondary" on:click={() => (confirmReset = false)}>Cancel</Button>
      </div>
    </div>
  </div>
{/if}

<MessageModal
  show={confirmCatalogSwitch}
  title="Change AWS Catalog"
  message="Changing the AWS catalog will delete all local opportunity data and related records from the internal database. This prevents Sandbox and Production catalog records from being mixed or synced into the wrong AWS catalog."
  variant="danger"
  confirmLabel="Delete Local Data & Save"
  cancelLabel="Cancel"
  onConfirm={confirmAndSaveCatalogSwitch}
  onClose={() => (confirmCatalogSwitch = false)}
/>
